import {_} from 'lodash';
import {IMessage, IRouteDefinitionParams, ITableColumn} from "../../index";
import {ICell, tpl, viewModel as SearchViewModel} from "../search/search";
import {global, lm} from "../../appContext";
import pubsub from "../../pubsub";
import {nsClone} from "../../clone/fn";
import {nsFa} from "../fa/fn";
import {nsMessages} from "./fn";
import {getRouteDropdownButton, getRouteHash, setRouteHash} from "../../constants/RouteDefinitions";
import {ISearchRowButtons} from "../search/index";
import {ObjectTypeId} from "../../constants";
import {nsMessageFlowClone} from "../../clone/nsMessageFlowClone";
import {isSupportedCircle} from "../../util/misc";
import * as ko from "knockout";
import {ISplitButtonParams} from "@component/button/splitButton/split-button";
import {saveMessageFlowName} from "../search/inlineRenameSave";
import i = nsFa.i;
import getMainType = nsMessages.getMainType;
import getCustomMessageObject = nsMessages.getCustomMessageObject;
import getAddRecipientLabel = nsMessages.getAddRecipientLabel;
export let template = tpl;

export class viewModel extends SearchViewModel<IMessage> {

    public TABLE: ITableColumn[] = [
        {
            key: "Id",
        },
        {
            key: "Name",
            getComponent: (item: IMessage) => ({
                name: 'component/search/inline-rename',
                params: {
                    item,
                    canEdit: () => global.permissionValidator.isMinCompanyAdmin(),
                    lockedTitleKey: 'messageflow.rename.locked',
                    searchKey: this.searchkey,
                    maxLength: 255,
                    save: (newName: string) => saveMessageFlowName(item, newName),
                },
            }),
        },
        {
            key: "subject",
        },
        {
            key: "recipientfield",
            html: true,
            binding: true,
            getContent: obj => {
                return `<span data-bind="_component: {name: 'component/variable/syncfield-name', params: {key: '${obj.recipientfield}', objecttypeid: ${ObjectTypeId.User}}}"></span>`
            }
        },
        {
            key: "addrecipient",
            label: 'recipient|p',
            count: true,
        },
        {
            key: "messagetypename",
            label: 'type',
            center: true,
        },
        // logcount represents the total number of messages sent during the lifetime of the message flow
        // (vs. the count in details view - showing the messages sent during the last 30 days)
        {
            key: 'logcount',
            label: 'log',
            css: 'cell__func'
        },
        {
            key: 'active',
            label: 'active',
            center: true,
        },
        {
            key: "func",
            label: '',
            validator: () => global.permissionValidator.isMinSuperManager() && !this.selectedObjects().length,
        }
    ];

    protected override fnRow: KnockoutObservableArray<ISearchRowButtons> = ko.observableArray<ISearchRowButtons>([]);

    constructor(params: IRouteDefinitionParams) {
        super(params);

        this.populatePageButtons();
        this.kosub.subscribe(global.login.selectedOrganization, () => this.populatePageButtons());

        pubsub.on(this, "messagetemplate.copy.success", this.searchChangedHandler.bind(this));
        pubsub.on(this, "messagetemplate.delete.success", () => this.preSubmit(true, true, 0));
    }

    protected populatePageButtons() {
        this.fnRow.removeAll();

        const pv = global.permissionValidator;
        this.fnRow([
            {
                icon: 'fa-ellipsis-v',
                title: 'function|p',
                getComponent: (messageTemplate: IMessage) => {
                    return {
                        name: 'component/button/ellipsis-dropdown',
                        params: {
                            buttons: [
                                {
                                    label: 'edit',
                                    href: () => getRouteHash(['child', 'edit'], this.__routeDef, this.__routeParams, true, messageTemplate),
                                    validator: () => pv.isMinCompanyAdmin(),
                                },
                                {
                                    label: 'copy',
                                    onClick: () => nsMessages.copyMessageDialogWithoutLog(messageTemplate),
                                    validator: () => pv.isMinCompanyAdmin(),
                                },
                                {
                                    label: 'preview',
                                    onClick: () => this.onPreviewClick(messageTemplate),
                                    validator: () => pv.isMinSuperManager(),
                                },
                                {
                                    label: 'send message',
                                    onClick: () => {
                                        setRouteHash('messageflowdetails', messageTemplate)
                                    },
                                    validator: () => pv.isMinSuperManager(),
                                },
                                {
                                    label: 'delete',
                                    cls: 'btn-danger btn-ready-calm',
                                    onClick: () => nsMessageFlowClone.trashDialog(messageTemplate),
                                    validator: () => pv.isMinCompanyAdmin(),
                                },
                            ],
                        },
                    }
                }
            },
        ])
    }

    protected override getSearchSplitButtonParams(): ISplitButtonParams | null {
        return null;
    }

    protected override getSearchHeaderSplitButtonParams(): ISplitButtonParams | null {
        return {
            mainButton: getRouteDropdownButton('createmessageflow', {cls: 'btn-action', icon: 'fa-plus'}),
        }
    }

    private searchChangedHandler() {
        if (global.login.selectedOrganization()?.organizationid === nsClone.targetOrg()?.organizationid) {
            this.preSubmit(true, true,0);
        }
    }

    private onPreviewClick(message: IMessage) {
        global.dialogManager.addComponent({
            title: lm.t(`${i('fa-eye')} ((preview message)) [${message.name}]`) ?? '',
            fullWidth: true,
            content: {
                name: 'component/messages/preview',
                params: {message: message},
            }
        });
    }

    protected override getCellContent(obj: ICell): string | number {
        const val = this.getCellVal(obj)
        switch (obj.key) {
            case 'active':
                return isSupportedCircle(val)
            case "addrecipient":
                if (val.length) {
                    const main = getMainType(obj.item.messagetypename);
                    const num = val.split(',').length;
                    return main ? `${i(main.icon)} ${num}` : String(num);
                } else {
                    return '';
                }
            case 'messagetypename':
                return i(getCustomMessageObject(val).icon);
        }
        return super.getCellContent(obj);
    }

    protected override getCellText(obj: ICell<IMessage>): string {
        const val = this.getCellVal(obj)
        switch (obj.key) {
            case "addrecipient":
                if (val.length) {
                    return lm.t(getAddRecipientLabel((obj.item as IMessage).messagetypeid)) + ':\n' +
                        _(val.split(','))
                            .map((str: string) => '- ' + str.trim())
                            .sortBy((str: string) => str.toLowerCase())
                            .value()
                            .join('\n');
                } else {
                    return '';
                }
            case 'active':
                return lm.t(val ? 'yes': 'no') ?? '';
            case 'messagetypename':
                return getCustomMessageObject(val).label;
        }
        return super.getCellText(obj);
    }
}
